import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { redirectSigninPage } from '@zep/common/routes';
import { PageWithLayout } from '@zep/types';

import { useAuth } from '../hooks';

/**
 * 로그인 되어 있는 유저만 접근 가능한 페이지에 사용합니다. (ex: 대시보드, 프로필)
 * @param redirectTo 로그인 되어 있지 않은 유저가 redirect 될 페이지(default: /accounts/signin)
 * @param suspense 로그인 되어 있지 않은 유저가 접근했을 때 fallback을 보여줄지 여부
 */

type PageAuthGuardProps = PropsWithChildren<Omit<AuthGuardOptions, 'fallback'>>;

export const PageAuthGuard = ({
  suspense = false,
  ...props
}: PageAuthGuardProps) => {
  if (suspense) {
    return <SuspenseAuthGuard {...props} />;
  }
  return <BlockingAuthGuard {...props} />;
};

const BlockingAuthGuard = ({ redirectTo, children }: PageAuthGuardProps) => {
  const { isReady } = useRouter();
  const [checked, setChecked] = useState(false);
  const { isAuthenticated, isInitialized } = useAuth();

  useEffect(() => {
    if (isReady && isInitialized) {
      if (!isAuthenticated) {
        redirectSigninPage(redirectTo);
      }
      setChecked(true);
    }
  }, [isReady, isInitialized, isAuthenticated, redirectTo]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
};

const SuspenseAuthGuard = ({
  redirectTo,
  children,
}: PropsWithChildren<Pick<AuthGuardOptions, 'redirectTo'>>) => {
  const { isReady } = useRouter();
  const { isInitialized, isAuthenticated } = useAuth();

  if (!isReady || !isInitialized) {
    throw Promise.resolve();
  } else if (!isAuthenticated) {
    redirectSigninPage(redirectTo);
    throw Promise.resolve();
  }

  return children;
};

type AuthGuardOptions = {
  redirectTo?: string;
  suspense?: boolean;
};

export const withPageAuthGuard = <
  Props = Record<string, any>,
  InitialProps = Props,
>(
  Page: PageWithLayout<Props, InitialProps>,
  options?: AuthGuardOptions,
) => {
  return Object.assign(
    (props: PropsWithChildren<Props>) => (
      <PageAuthGuard
        redirectTo={options?.redirectTo}
        suspense={options?.suspense}>
        <Page {...props} />
      </PageAuthGuard>
    ),
    {
      getLayout: Page.getLayout,
    },
  );
};
